import { Box, Button, Checkbox, Modal, Text } from '@ksyos/design-system';
import { useEffect, useState } from 'react';
import type { ICheckedTreatment } from '@common/interfaces/checkedTreatment';
import type { ITherapielandTreatment } from '@common/utils/therapielandTreatments';
import { TherapielandTreatmentCategory } from '@common/enums';
import { fetchJson } from '@frontend/store/fetch';
import { getSelectedLanguage } from '../../translate';

interface ITherapielandProgrammasModalProps {
    checkedTreatments: ICheckedTreatment[];
    handleChange: (checkedTreatments: ICheckedTreatment[]) => Promise<void>;
    handleClose: () => void;
    isOpen: boolean;
    patientId: string;
}

const osasTherapieLandModulesNames = [
    'Leefstijl',
    'Bewegen',
    'Piekeren',
    'Ontspanning',
    'Slaap & shifts',
    'Slaaprestrictie',
    'Lekker slapen',
    'Slaapapneu',
];

export function TherapielandProgrammasModal(props: Readonly<ITherapielandProgrammasModalProps>) {
    const translate = getSelectedLanguage();

    const [treatments, setTreatments] = useState<ITherapielandTreatment[]>([]);
    const [checkedTreatmentsInModal, setCheckedTreatmentsInModal] = useState<ICheckedTreatment[]>(
        props.checkedTreatments
    );
    const [previouslyCompletedTreatments, setPreviouslyCompletedTreatments] = useState<ICheckedTreatment[]>([]);

    useEffect(() => {
        getTreatments();
    }, []);

    return (
        <Modal
            isOpen={props.isOpen}
            onClose={props.handleClose}
            size="small"
        >
            <Modal.Header>{translate.diagnosticReport.therapyLandProgramme}</Modal.Header>
            <Modal.CloseButton />
            <Modal.Body>
                <Text mb={2}>{translate.diagnosticReport.selectProgramme}</Text>
                <Box
                    display="flex"
                    flexDirection="column"
                    gap={2}
                >
                    {renderTherapielandProgrammas()}
                </Box>
            </Modal.Body>
            <Modal.Footer>
                <Button
                    onClick={() => props.handleClose()}
                    variant="outline"
                >
                    {translate.common.cancel}
                </Button>
                <Button
                    className="automation-therapieland-programmas-modal-button-save"
                    onClick={() => props.handleChange(checkedTreatmentsInModal)}
                >
                    {translate.common.save}
                </Button>
            </Modal.Footer>
        </Modal>
    );

    function renderTherapielandProgrammas() {
        const checkboxes = treatments.map((treatment) => {
            const isPreviouslyCompletedTreatment = previouslyCompletedTreatments.some(
                (completedTreatment) => completedTreatment.id === treatment.id
            );

            return (
                <Box
                    display="flex"
                    flexDirection="column"
                    key={treatment.id}
                >
                    <Checkbox
                        checked={
                            checkedTreatmentsInModal.some((checkedTreatment) => checkedTreatment.id === treatment.id) ||
                            isPreviouslyCompletedTreatment
                        }
                        disabled={isPreviouslyCompletedTreatment}
                        key={treatment.id}
                        label={`${treatment.name}${isPreviouslyCompletedTreatment ? ' - reeds verstuurd' : ''}`}
                        onChange={(e) => onChange(e.target.checked, treatment)}
                    />
                </Box>
            );
        });

        return <>{checkboxes}</>;
    }

    function onChange(value: boolean, treatment: ITherapielandTreatment) {
        if (value) {
            setCheckedTreatmentsInModal([
                ...checkedTreatmentsInModal,
                { category: treatment.categories[0].name, id: treatment.id, treatmentName: treatment.name },
            ]);
        } else {
            setCheckedTreatmentsInModal(
                checkedTreatmentsInModal.filter((checkedTreatment) => treatment.id !== checkedTreatment.id)
            );
        }
    }

    async function getTreatments() {
        const result = await fetchJson('/api/therapieland/getTreatments', 'GET');
        if (result.json?.length > 0) {
            const sleepTreatments: ITherapielandTreatment[] = result.json.filter((treatment: ITherapielandTreatment) =>
                osasTherapieLandModulesNames.includes(treatment.name)
            );
            setTreatments(sleepTreatments);
            const duplicateTreatments = await fetchJson('/api/therapieland/checkDuplicateTreatments', 'POST', {
                checkedTreatments: sleepTreatments.map((treatment) => ({
                    category: TherapielandTreatmentCategory.volwassenen,
                    id: treatment.id,
                    treatmentName: treatment.name,
                })),
                patientId: props.patientId,
            });
            setPreviouslyCompletedTreatments(duplicateTreatments.json?.listOfDuplicateTreatments ?? []);
        }
    }
}
